import React, { useState, useEffect } from "react";

import { API, graphqlOperation } from "aws-amplify";
import { inventoryItemByAisleByStatusForUpdate } from "../../../../graphql/inventory/custom-queries";
import { aisleByActiveByAreaBySortKey } from "../../../../graphql/reloadyard/custom-queries";
import { updateLocation } from "../../../../graphql/mutations";
import { updateInventoryItem } from "../../../../graphql/inventory/custom-mutations";
import { performUpdateInventoryMini } from "../../../../graphql/inventory/inventoryCRUD";

import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from "@material-ui/core";
import { Confirm, useNotify } from "react-admin";
import { logError } from "../../../../utils/appUtils";
import { InventoryStatus, LocationType } from "../../../../utils/constants";
import {
  getLocationFullName,
  setInventoryAisleList,
} from "../../../../utils/locationUtils";

export const RelocateBayDialog = ({
  bayNumber,
  bayList,
  setBayList,
  relocateOpen,
  setRelocateOpen,
  handleClose,
  classes,
}) => {
  const [aisles, setAisles] = useState([]);
  const [bays, setBays] = useState([]);
  const [fromAisle, setFromAisle] = useState();
  const [fromBay, setFromBay] = useState([]);
  const [fromLocations, setFromLocations] = useState();
  const [selectedAisle, setSelectedAisle] = useState();
  const [selectedBay, setSelectedBay] = useState([]);
  const [toBay, setToBay] = useState();
  const [inventoryItems, setInventoryItems] = useState();
  const [fromLocationToLocationMap, setFromLocationToLocationMap] = useState();
  const [error, setError] = useState([]);
  const [loading, setLoading] = useState();
  const [isConfirmOpen, setIsConfirmOpen] = useState();
  const notify = useNotify();

  useEffect(() => {
    if (bayList.length > 0) getAisles();
  }, []);

  const getAisles = async () => {
    try {
      setLoading(true);
      const bay = bayList[bayNumber];
      setFromBay(bay);
      const location = bay[0][0];
      const bayLocations = [];
      bay.forEach((level) =>
        level.forEach((location) => bayLocations.push(location))
      );
      setFromLocations(bayLocations);
      const response = await API.graphql(
        graphqlOperation(aisleByActiveByAreaBySortKey, {
          areaId: location.areaId,
          activeSortKey: { beginsWith: { active: "true" } },
        })
      );
      const fromAisle = response.data.aisleByActiveByAreaBySortKey.items.find(
        (itm) => itm.id === location.aisleId
      );
      setFromAisle(fromAisle);
      let aisles = response.data.aisleByActiveByAreaBySortKey.items.filter(
        (itm) =>
          itm.type === LocationType.STRUCTURED &&
          itm.id !== location.aisleId &&
          Boolean(itm.topDownPickOrder) === Boolean(fromAisle.topDownPickOrder)
      );

      if (fromAisle.locationLoad.container.selected) {
        aisles = aisles.filter((itm) => itm.locationLoad.container.selected);
      } else {
        aisles = aisles.filter((itm) => !itm.locationLoad.container.selected);
      }
      setAisles(aisles);
    } catch (err) {
      logError("reloadYard:Location:getAisles", err, notify);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    setIsConfirmOpen(true);
  };

  const relocateBayInventories = async () => {
    setIsConfirmOpen(false);
    setLoading(true);
    try {
      debugger;
      let inventoryItemList = inventoryItems;
      if (!inventoryItemList) {
        inventoryItemList = await getInventoryItems();
      }
      if (inventoryItemList.length === 0) {
        notify(`Bay ${bayNumber + 1} doesn't contain inventories.`);
        return;
      }
      const updateInventoryItemsList = [...inventoryItemList];
      const batchSize = 3;
      let updateInventoryPromiseList = [];
      while (updateInventoryItemsList.length > 0) {
        const updateList = updateInventoryItemsList.splice(0, batchSize);
        const batchUpdatePromiseList = updateInventoryItems(updateList);
        updateInventoryPromiseList = [
          ...updateInventoryPromiseList,
          ...batchUpdatePromiseList,
        ];
      }
      const responseList = await Promise.allSettled(updateInventoryPromiseList);
      debugger;
      const resError = responseList.find((itm) => itm.status === "rejected");
      if (resError) throw resError;
      else {
        await updateInventoryAisleList(responseList);
        notify(
          `Inventories relocated from aisle ${fromAisle?.name} bay ${
            bayNumber + 1
          } to aisle ${selectedAisle?.name} bay ${selectedBay}`
        );
      }

      fromBay.forEach((level) => {
        level.forEach((location) => (location.capacityUsed = 0));
      });

      bayList[bayNumber] = fromBay;
      setBayList([...bayList]);
    } catch (err) {
      logError("reloadYard:Location:relocateBayInventories", err, notify);
    } finally {
      setLoading(false);
      handleCancel();
    }
  };

  const updateInventoryAisleList = async (responseList) => {
    const inventoryMap = {};
    const promiseList = [];
    // keep the last inventoryItem update to get latest inventory.aisleList
    for (const response of responseList) {
      const inventoryItem = response.value?.data?.updateInventoryItem;
      if (response.status === "fulfilled" && inventoryItem) {
        const toUpdateInventory = {
          id: inventoryItem.inventoryId,
          aisleList: inventoryItem.inventory.aisleList,
          inventoryItems: inventoryItem.inventory.inventoryItems,
        };
        inventoryMap[toUpdateInventory.id] = toUpdateInventory;
      }
    }

    const inventoryList = Object.values(inventoryMap);
    for (const inventory of inventoryList) {
      setInventoryAisleList(inventory);

      const inventoryUpdatePromise = performUpdateInventoryMini(inventory);
      promiseList.push(inventoryUpdatePromise);
    }

    await Promise.all(promiseList);
  };

  const updateInventoryItems = (updateList) => {
    let promiseList = [];
    updateList.forEach((inventoryItem) => {
      const inventoryItemPromiseList = processInventoryItem(inventoryItem);
      promiseList = [...promiseList, ...inventoryItemPromiseList];
    });
    return promiseList;
  };

  const processInventoryItem = (inventoryItem) => {
    const newLocation = fromLocationToLocationMap[inventoryItem.locationId];
    newLocation.capacityUsed = inventoryItem.location.capacityUsed;
    const toLocationPromise = doUpdateLocation(newLocation);
    inventoryItem.location.capacityUsed = 0;
    const fromLocationPromise = doUpdateLocation(inventoryItem.location);
    newLocation.area = selectedAisle.area;
    newLocation.zone = selectedAisle.zone;
    newLocation.aisle = selectedAisle;
    const toUpdateInventoryItem = {
      ...inventoryItem,
      locationFullName: getLocationFullName(newLocation),
      locationId: newLocation.id,
      aisleId: newLocation.aisleId,
      zoneId: newLocation.zoneId,
    };
    delete toUpdateInventoryItem.area;
    delete toUpdateInventoryItem.createdAt;
    delete toUpdateInventoryItem.inventory;
    delete toUpdateInventoryItem.location;
    delete toUpdateInventoryItem.seals;
    delete toUpdateInventoryItem.updatedAt;
    delete toUpdateInventoryItem.zone;
    const updateInventoryItemPromise = API.graphql(
      graphqlOperation(updateInventoryItem, {
        input: toUpdateInventoryItem,
      })
    );
    return [updateInventoryItemPromise, toLocationPromise, fromLocationPromise];
  };

  const doUpdateLocation = (location) => {
    const toUpdateLocation = {
      ...location,
      productId: location.productId ? location.productId : null,
    };
    delete toUpdateLocation.aisle;
    delete toUpdateLocation.area;
    delete toUpdateLocation.zone;
    delete toUpdateLocation.createdAt;
    delete toUpdateLocation.updatedAt;
    return API.graphql(
      graphqlOperation(updateLocation, { input: toUpdateLocation })
    );
  };

  const handleAisleChange = (value) => {
    if (value) {
      const selectedAisle = aisles.find((itm) => itm.id === value);
      setSelectedAisle(selectedAisle);
      const baysMap = {};
      selectedAisle.locations.items.forEach(
        (itm) => (baysMap[itm.bay] = itm.bay)
      );
      const bays = Object.keys(baysMap);
      setBays(bays);
      setToBay(null);
    } else {
      setSelectedAisle(null);
    }
    setSelectedBay("");
    setError("");
  };

  const validateLocationCounts = (toLocations, fromLocations) => {
    if (toLocations.length < fromLocations.length) return false;
    return true;
  };

  const sortAndGroupLocations = (toLocations) => {
    toLocations.sort((a, b) => {
      if (a.level !== b.level) return a.level - b.level;
      if (a.position !== b.position) return a.position - b.position;
      return 0;
    });

    const toBay = [];
    toLocations.forEach((itm) => {
      if (itm.level > toBay.length) toBay.push([]);
      toBay[itm.level - 1].push(itm);
    });
    return toBay;
  };

  const validateBayStructure = (toBay, fromBay) => {
    if (toBay.length < fromBay.length) return false;

    for (let i = 0; i < fromBay.length; i++) {
      if (fromBay[i].length > toBay[i].length) {
        return false;
      }
    }
    return true;
  };

  const validateLocationLoadTypes = (fromBay, toBay) => {
    const fromLocationToLocationMap = {};

    for (let levelIdx = 0; levelIdx < fromBay.length; levelIdx++) {
      for (
        let positionIdx = 0;
        positionIdx < fromBay[levelIdx].length;
        positionIdx++
      ) {
        const oldLocation = fromBay[levelIdx][positionIdx];
        const newLocation = toBay[levelIdx][positionIdx];

        if (!isLocationLoadCompatible(oldLocation, newLocation)) {
          return { hasError: true, map: null };
        }
        fromLocationToLocationMap[oldLocation.id] = newLocation;
      }
    }
    return { hasError: false, map: fromLocationToLocationMap };
  };

  const isLocationLoadCompatible = (oldLocation, newLocation) => {
    const isOtherCompatible =
      Boolean(newLocation.locationLoad.other.selected) ===
        Boolean(oldLocation.locationLoad.other.selected) &&
      newLocation.locationLoad.other.capacity ===
        oldLocation.locationLoad.other.capacity;

    const isPalletCompatible =
      Boolean(newLocation.locationLoad.pallet.selected) ===
        Boolean(oldLocation.locationLoad.pallet.selected) &&
      newLocation.locationLoad.pallet.capacity ===
        oldLocation.locationLoad.pallet.capacity;

    const isBoxCompatible =
      Boolean(newLocation.locationLoad.box.selected) ===
        Boolean(oldLocation.locationLoad.box.selected) &&
      newLocation.locationLoad.box.capacity ===
        oldLocation.locationLoad.box.capacity;

    const isSuperSackCompatible =
      Boolean(newLocation.locationLoad.superSack.selected) ===
        Boolean(oldLocation.locationLoad.superSack.selected) &&
      newLocation.locationLoad.superSack.capacity ===
        oldLocation.locationLoad.superSack.capacity;

    return (
      isPalletCompatible &&
      isBoxCompatible &&
      isSuperSackCompatible &&
      isOtherCompatible
    );
  };

  const handleBayChange = async (value) => {
    setError("");
    setSelectedBay(value);

    // Get available locations in the selected bay
    const toLocations = [...selectedAisle.locations.items].filter(
      (itm) => itm.bay === Number(value) && !itm.capacityUsed
    );

    // Validate location counts
    if (!validateLocationCounts(toLocations, fromLocations)) {
      setError(
        `Invalid selection, bay ${value} doesn't contain enough locations.`
      );
      return;
    }

    // Sort and group locations
    const toBay = sortAndGroupLocations(toLocations);

    // Validate bay structure
    if (!validateBayStructure(toBay, fromBay)) {
      setError(
        `Invalid selection, bay ${value} doesn't have a compatible structure.`
      );
      return;
    }

    // Validate location load types if not container selected
    if (!fromAisle.locationLoad.container.selected) {
      const { hasError, map } = validateLocationLoadTypes(fromBay, toBay);
      if (hasError) {
        setError(
          `Invalid selection, bay ${value} has an incompatible load type.`
        );
        return;
      }
      setFromLocationToLocationMap(map);
    }

    // Check inventory accounts if zone has accountId
    if (selectedAisle.zone.accountId) {
      await checkInventoryAccounts();
    }

    setToBay(toBay);
  };

  const checkInventoryAccounts = async () => {
    try {
      setLoading(true);
      const inventoryItems = await getInventoryItems();
      if (
        inventoryItems.find(
          (itm) => itm.accountId !== selectedAisle.zone.accountId
        )
      ) {
        setError(
          `Invalid selection, aisle ${selectedAisle.name} can't be used for bay ${selectedBay} inventories.`
        );
      } else {
        setInventoryItems(inventoryItems);
      }
    } catch (err) {
      logError("reloadYard:Location:checkInventoryAccounts", err, notify);
    } finally {
      setLoading(false);
    }
  };

  const getInventoryItems = async () => {
    try {
      setLoading(true);
      const statusQueries = [
        InventoryStatus.UNLOADED,
        InventoryStatus.REVIEW,
        InventoryStatus.STORED,
      ];
      let inventoryItemsList = [];
      for (const queryStatus of statusQueries) {
        const searchInput = {
          aisleId: fromAisle.id,
          status: { beginsWith: queryStatus },
          limit: 100,
        };
        let nextToken = true;
        while (nextToken) {
          const input =
            nextToken === true ? searchInput : { ...searchInput, nextToken };
          const results = await API.graphql(
            graphqlOperation(inventoryItemByAisleByStatusForUpdate, input)
          );
          inventoryItemsList.splice(
            -1,
            0,
            ...results.data.inventoryItemByAisleByStatus.items
          );
          nextToken = results.data.inventoryItemByAisleByStatus.nextToken;
        }
      }
      inventoryItemsList = inventoryItemsList.filter(
        (itm) => itm.location.bay === bayNumber + 1
      );
      debugger;
      return inventoryItemsList;
    } catch (err) {
      logError("reloadYard:Location:getInventoryItems", err, notify);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setRelocateOpen(false);
    handleClose();
  };

  return (
    <>
      <Dialog open={relocateOpen} onClose={handleClose} fullWidth maxWidth="md">
        <DialogTitle id="relocate-inventories">
          Relocate inventories
        </DialogTitle>
        <DialogContent>
          <Backdrop className={classes.backdrop} open={loading}>
            <CircularProgress color="inherit" />
          </Backdrop>
          <Grid container spacing={1}>
            <Grid itm xs={6} md={3}>
              <TextField
                id="from-aisle"
                label="From aisle"
                margin="dense"
                fullWidth
                variant="outlined"
                value={fromAisle ? fromAisle.name : ""}
                disabled={true}
                className={classes.dialogInput}
              />
            </Grid>
            <Grid itm xs={6} md={3}>
              <TextField
                id="from-bay"
                label="From bay"
                margin="dense"
                fullWidth
                variant="outlined"
                value={bayNumber + 1}
                disabled={true}
                className={classes.dialogInput}
              />
            </Grid>
            <Grid itm xs={6} md={3}>
              <TextField
                id="to-aisle"
                label="To aisle"
                margin="dense"
                fullWidth
                variant="outlined"
                select
                required
                autoFocus
                className={classes.dialogInput}
                value={selectedAisle ? selectedAisle.id : ""}
                onChange={(evt) => handleAisleChange(evt.target.value)}
              >
                {aisles &&
                  [
                    { id: "", value: "", name: <span>&nbsp;</span> },
                    ...aisles,
                  ].map((itm) => (
                    <MenuItem key={itm.id} value={itm.id}>
                      {itm.name}
                    </MenuItem>
                  ))}
              </TextField>
            </Grid>
            <Grid itm xs={6} md={3}>
              <TextField
                id="to-bay"
                label="To bay"
                margin="dense"
                fullWidth
                variant="outlined"
                select
                required
                disabled={!selectedAisle}
                className={classes.dialogInput}
                value={selectedBay ? selectedBay : ""}
                onChange={(evt) => handleBayChange(evt.target.value)}
              >
                {bays.map((itm) => (
                  <MenuItem key={itm} value={itm}>
                    {itm}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
          <Box mt={1} mb={1}>
            {error && (
              <Typography
                color="error"
                variant="subtitle2"
                className={classes.tagsTitle}
              >
                {error}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            color="primary"
            disabled={error || loading || !toBay}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>
      <Confirm
        isOpen={isConfirmOpen}
        loading={loading}
        title={"Confirm relocate"}
        content={`Relocate inventories from aisle ${fromAisle?.name} bay ${
          bayNumber + 1
        } to aisle ${selectedAisle?.name} bay ${selectedBay}?`}
        onConfirm={relocateBayInventories}
        onClose={() => setIsConfirmOpen(false)}
      />
    </>
  );
};
